import { createEffect, createSignal, onCleanup } from 'solid-js';

// Overlay plumbing shared by the overlay shells (`testing/overlay-shells.ts`):
// each takes `useScrollLock`, and all but the nav drawer (`AppSidebar`, which
// never unmounts) take `useClosing` — the palette's through `CommandPalette`.

/**
 * Exit-animation gate (spec §6): mirrors `open` but delays the false edge by
 * `ms` so the `--closing` CSS animation plays before unmount. Render behind
 * `visible()`; add the closing modifier from `closing()`.
 */
export function useClosing(
  open: () => boolean,
  ms: number,
): { visible: () => boolean; closing: () => boolean } {
  const [visible, setVisible] = createSignal(open());
  const [closing, setClosing] = createSignal(false);
  createEffect(() => {
    if (open()) {
      setVisible(true);
      setClosing(false);
      return;
    }
    if (!visible()) return;
    setClosing(true);
    const timer = setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, ms);
    // A reopen mid-exit re-runs the effect; drop the stale unmount timer first.
    onCleanup(() => clearTimeout(timer));
  });
  return { visible, closing };
}

/**
 * How many overlays hold the body's scroll lock, and what `overflow` read
 * before the first of them took it. Module-level because overlays STACK and do
 * not close in order: a self-closing modal can close under the ⌘K palette that
 * opened over it. A per-layer save-and-restore let the modal "restore" `''`
 * under the open palette, then the palette restore the `hidden` it had saved,
 * leaving the page unscrollable until a reload (plan 101 slice 19, 19-5).
 */
let locks = 0;
let unlocked = '';

/** Lock body scroll while an overlay is open: the first lock sets it, the last release clears it. */
export function useScrollLock(open: () => boolean): void {
  createEffect(() => {
    if (!open()) return;
    if (locks === 0) {
      unlocked = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
    locks += 1;
    onCleanup(() => {
      locks -= 1;
      if (locks === 0) document.body.style.overflow = unlocked;
    });
  });
}
